/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* ── OpenAI-Ads click id (oppref) ─────────────────────────────────────
 * ChatGPT ads append ?oppref=<click id> to the landing URL. The value is carried
 * into the cart attribute "oppref" (-> order note_attributes) by the ChannelManager
 * below, in the same cart/update.js request as channel/market_domain/price_source.
 * It never touches channel or price_source and therefore never changes prices.
 * Without consent: read from the URL and set as cart attribute right away, nothing
 * is stored on the device. Persistence beyond the cart: localStorage "mc_oppref" for
 * max. 30 days, ONLY with marketing consent (Pandectes "Targeting" /
 * Shopify marketingAllowed) - decided by Ulrich Seidel 13.10.2026. Background: docs/oppref-openai-ads-click-id.md
 */
const OpprefStore = (function () {
  const PARAM = 'oppref';
  const STORAGE_KEY = 'mc_oppref';
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const MAX_LENGTH = 1024;

  function isValid(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= MAX_LENGTH && !/[\s<>"']/.test(value);
  }

  // Percent-decoded but otherwise verbatim. Deliberately not URLSearchParams: that
  // turns a literal "+" (base64) into a space and would alter the click id.
  function getUrlValue() {
    try {
      const pairs = window.location.search.replace(/^\?/, '').split('&');
      for (let i = 0; i < pairs.length; i++) {
        const eq = pairs[i].indexOf('=');
        if (eq > 0 && pairs[i].slice(0, eq) === PARAM) {
          const value = decodeURIComponent(pairs[i].slice(eq + 1));
          return isValid(value) ? value : '';
        }
      }
    } catch (e) {
      // malformed percent-encoding
    }
    return '';
  }

  function consentAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
    } catch (e) {
      return false;
    }
  }

  function clearStored() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // localStorage unavailable
    }
  }

  function getStoredValue() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return '';
      }
      const entry = JSON.parse(raw);
      if (!entry || !isValid(entry.v) || typeof entry.t !== 'number' || Date.now() - entry.t > MAX_AGE_MS) {
        clearStored();
        return '';
      }
      return entry.v;
    } catch (e) {
      clearStored();
      return '';
    }
  }

  // The 30-day window starts with the click: an already stored identical value keeps its timestamp.
  function remember(value) {
    if (!isValid(value) || !consentAllowed()) {
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const entry = raw ? JSON.parse(raw) : null;
      if (entry && entry.v === value && typeof entry.t === 'number') {
        return;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: value, t: Date.now() }));
    } catch (e) {
      // localStorage unavailable
    }
  }

  function getCartValue() {
    return window.theme && typeof window.theme.cartOppref === 'string' ? window.theme.cartOppref : '';
  }

  // Shopify loads the Customer Privacy API asynchronously - at DOMContentLoaded it is
  // usually not there yet, so every consent decision has to wait for it.
  function whenConsentReady(callback) {
    let done = false;
    const run = function () {
      if (!done) {
        done = true;
        callback();
      }
    };
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.marketingAllowed === 'function') {
        run();
        return;
      }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], run);
        setTimeout(run, 5000);
        return;
      }
    } catch (e) {
      // fall through
    }
    run();
  }

  // Remembered click id (consent only), delivered once the consent state is known.
  // A click id in the URL is remembered at the same moment.
  function resolveStored(callback) {
    whenConsentReady(function () {
      const urlValue = getUrlValue();
      if (urlValue) {
        remember(urlValue);
        return;
      }
      const stored = consentAllowed() ? getStoredValue() : '';
      if (stored) {
        callback(stored);
      }
    });
  }

  // Consent is often given after page load: persist the click id of this page then,
  // and drop a stored one if consent is withdrawn.
  document.addEventListener('visitorConsentCollected', function () {
    if (consentAllowed()) {
      remember(getUrlValue() || getCartValue());
    } else {
      clearStored();
    }
  });

  return {
    getUrlValue: getUrlValue,
    resolveStored: resolveStored,
    getCartValue: getCartValue,
  };
})();

/* ── Channel Manager ─────────────────────────────────────────────────── */
const ChannelManager = (function () {
  const SESSION_CHANNEL_KEY = 'mc_active_channel';

  let syncedCartChannel = window.theme && typeof window.theme.cartChannel === 'string'
    ? window.theme.cartChannel
    : '';
  let syncedMarketDomain = window.theme && typeof window.theme.cartMarketDomain === 'string'
    ? window.theme.cartMarketDomain
    : '';
  let syncedPriceSource = window.theme && typeof window.theme.cartPriceSource === 'string'
    ? window.theme.cartPriceSource
    : '';
  let syncedOppref = OpprefStore.getCartValue();

  function getSessionChannel() {
    try {
      return sessionStorage.getItem(SESSION_CHANNEL_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function storeSessionChannel(channel) {
    try {
      if (channel) {
        sessionStorage.setItem(SESSION_CHANNEL_KEY, channel);
      } else {
        sessionStorage.removeItem(SESSION_CHANNEL_KEY);
      }
    } catch (e) {
      // sessionStorage unavailable
    }
  }

  // Mirrors the Liquid normalisation (downcase | replace: '_','-' | handle) so the
  // attribute written to the cart is identical to the channel the page rendered with.
  function normalizeChannel(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // An explicit /products/{handle}/{channel} entry point. Kept separate from
  // getPathChannel() because it outranks the cart attribute — see snippets/mc-channel-current.liquid.
  function getExplicitPathChannel() {
    const segments = window.location.pathname.split('/');
    if (segments.length > 3 && segments[1] === 'products' && segments[segments.length - 1]) {
      return normalizeChannel(segments[segments.length - 1]);
    }

    return '';
  }

  function getPathChannel() {
    if (window.theme && typeof window.theme.pathChannel === 'string' && window.theme.pathChannel !== '') {
      return window.theme.pathChannel;
    }

    const segments = window.location.pathname.split('/');
    if (segments.length > 2 && segments[1] === 'collections' && segments[2]) {
      return normalizeChannel(segments[2]);
    }

    return getExplicitPathChannel();
  }

  function getCartChannel() {
    return window.theme && typeof window.theme.cartChannel === 'string'
      ? window.theme.cartChannel
      : '';
  }

  function getChannel() {
    return getExplicitPathChannel() || getCartChannel() || getSessionChannel() || getPathChannel();
  }

  function getMarketDomain() {
    if (window.theme && typeof window.theme.marketDomain === 'string' && window.theme.marketDomain !== '') {
      return window.theme.marketDomain;
    }

    return window.location.hostname.replace(/^www\./, '').toLowerCase();
  }

  function getPriceSource(channel) {
    return channel ? 'channel' : 'market';
  }

  function init() {
    const cartChannel = getCartChannel();
    const activeChannel = getExplicitPathChannel() || cartChannel || getSessionChannel() || getPathChannel();
    const marketDomain = getMarketDomain();
    const priceSource = getPriceSource(activeChannel);
    // A new click id in the URL goes into the cart right away (no consent needed for
    // the cart attribute); a remembered one follows once the consent state is known.
    const oppref = OpprefStore.getUrlValue();

    OpprefStore.resolveStored(function (stored) {
      syncCartAttributes(activeChannel, marketDomain, priceSource, stored);
    });

    if (activeChannel) {
      storeSessionChannel(activeChannel);
    }

    if (!activeChannel && !marketDomain && !oppref) {
      return;
    }

    syncCartAttributes(activeChannel, marketDomain, priceSource, oppref);
  }

  // Requests are chained: two parallel cart/update.js calls on a first visit (no cart
  // cookie yet) would create two carts and one set of attributes would be lost.
  let syncQueue = Promise.resolve();

  function syncCartAttributes(channel, marketDomain, priceSource, oppref) {
    syncQueue = syncQueue.then(function () {
      return doSyncCartAttributes(channel, marketDomain, priceSource, oppref);
    });
  }

  function doSyncCartAttributes(channel, marketDomain, priceSource, oppref) {
    // oppref is only ever set, never cleared here: an empty value must not wipe the
    // click id an earlier page of this cart already stored.
    const opprefPending = !!oppref && oppref !== syncedOppref;

    if (syncedCartChannel === channel && syncedMarketDomain === marketDomain && syncedPriceSource === priceSource && !opprefPending) {
      return;
    }

    const attributes = {
      channel: channel || '',
      market_domain: marketDomain || '',
      price_source: priceSource || ''
    };
    if (opprefPending) {
      attributes.oppref = oppref;
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: attributes
      }),
    }).then(function () {
      syncedCartChannel = channel;
      if (opprefPending) {
        syncedOppref = oppref;
        if (window.theme) {
          window.theme.cartOppref = oppref;
        }
      }
      syncedMarketDomain = marketDomain;
      syncedPriceSource = priceSource;
      if (window.theme) {
        window.theme.cartChannel = channel;
        window.theme.cartMarketDomain = marketDomain;
        window.theme.cartPriceSource = priceSource;
      }

      document.querySelectorAll('input[name="attributes[channel]"]').forEach(function (input) {
        input.value = channel;
      });
      document.querySelectorAll('input[name="attributes[market_domain]"]').forEach(function (input) {
        input.value = marketDomain;
      });
      document.querySelectorAll('input[name="attributes[price_source]"]').forEach(function (input) {
        input.value = priceSource;
      });

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          scrollToTop: false
        }
      }));
    }).catch(function (err) {
      console.error('ChannelManager: failed to sync cart attribute', err);
    });
  }

  document.addEventListener('DOMContentLoaded', init);

  return {
    getChannel: getChannel,
    getMarketDomain: getMarketDomain,
  };
})();

document.addEventListener('variant:changed', async function(event) {
    // Check if mc-product-meta is rendered by looking for the product-meta class
    const productMeta = document.querySelector('.product-meta');
    if (!productMeta) return;

    var variant = event.detail.variant;
    //console.error('variant change triggered', variant);

    // Add variant title update
    const variantTitleElement = document.querySelector('.product-meta__variant-title');
    if (variantTitleElement) {
        variantTitleElement.textContent = variant.title;
    }

    try {
        const currentShopLocale = document.documentElement.lang;
        const shopName = Shopify.shop;
        const response = await fetch(`/apps/mcb/variantinfo/${currentShopLocale}/${variant.id}?shop=${shopName}`);
        const variantData = await response.json();


        // Update variant meta reference section
        const referenceElement = document.querySelector('.product-meta__reference');
        if (referenceElement) {
            // Update fields (SKU, GTIN, MPN)
            const fields = {
                'sku': variantData.sku,
                'gtin': variantData.gtin,
                'mpn': variantData.mpn
            };

            //console.log('Fields to update:', fields);  // Log the fields object

            Object.entries(fields).forEach(([fieldType, value]) => {
                let element = referenceElement.querySelector(`[data-field-type="${fieldType}"]`);

                if (element) {
                    // First update the number
                    const numberElement = element.querySelector(`.product-meta__${fieldType}-number`);
                    if (numberElement) {
                        numberElement.textContent = value || '';
                    }

                    // Then update visibility based on value
                    element.style.display = value ? 'inline-block' : 'none';
                }
            });
        }

        // Update short description
        if (variantData.shortDescription) {
            let descriptionElement = document.querySelector('.product-meta__short-description');
            if (!descriptionElement) {
                descriptionElement = document.createElement('div');
                descriptionElement.className = 'product-meta__reference product-meta__short-description';
                descriptionElement.innerHTML = `<p>${variantData.shortDescription}</p>`;
                document.querySelector('.product-meta').appendChild(descriptionElement);
            } else {
                descriptionElement.querySelector('p').textContent = variantData.shortDescription;
            }
        } else {
            const descriptionElement = document.querySelector('.product-meta__short-description');
            if (descriptionElement) {
                descriptionElement.remove();
            }
        }
    } catch (error) {
        console.error('Error fetching variant info:', error);
    }
});

// First define your section class
class DeviceChooserSection extends HTMLElement {
    constructor() {
        super();
        // Your section initialization code here
    }

    connectedCallback() {
        // Setup code when section is mounted
    }

    disconnectedCallback() {
        // Cleanup code when section is unmounted
    }
}

class BreadcrumbsSection extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        // No special initialization needed since the breadcrumbs are static
    }

    disconnectedCallback() {
        // Cleanup code when section is unmounted
    }
}

class CompatibleDevicesSection extends HTMLElement {
    constructor() {
        super();
        this.columnCount = this.querySelector('.compatibility-columns').dataset.columns;
        this.updateLayout();
    }

    updateLayout() {
        const columnsContainer = this.querySelector('.compatibility-columns');
        if (columnsContainer) {
            columnsContainer.style.gridTemplateColumns = `repeat(${this.columnCount}, 1fr)`;
        }
    }

    connectedCallback() {
        // Add any initialization if needed
    }

    disconnectedCallback() {
        // Cleanup if needed
    }
}

class RelatedProductsSection extends HTMLElement {
    constructor() {
        super();
        this.autoSelectFirst = false;
        this.toggles = this.querySelectorAll('[data-vendor-toggle]');
        this.init();
    }

    init() {
        this.toggles.forEach(toggle => {
            toggle.addEventListener('click', () => this.toggleVendor(toggle));
        });
    }

    toggleVendor(toggle) {
        const vendorId = toggle.dataset.vendorToggle;
        const group = this.querySelector(`[data-vendor-group="${vendorId}"]`);

        // Close all other groups
        this.querySelectorAll('.vendor-group.is-active').forEach(activeGroup => {
            if (activeGroup !== group) {
                activeGroup.classList.remove('is-active');
                const activeToggle = this.querySelector(`[data-vendor-toggle="${activeGroup.dataset.vendorGroup}"]`);
                activeToggle.classList.remove('is-active');
            }
        });

        // Toggle current group
        toggle.classList.toggle('is-active');
        group.classList.toggle('is-active');

        // If autoSelectFirst is enabled and the group is being opened, navigate to the first product
        if (this.autoSelectFirst && group.classList.contains('is-active')) {
            const firstProduct = group.querySelector('.mc-related-product-item');
            if (firstProduct) {
                window.location.href = firstProduct.getAttribute('onclick').replace("window.location.href='", '').replace("'", '');
            }
        }
    }
}


class CollectionVariantSection extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        // No special initialization needed since the breadcrumbs are static
    }

    disconnectedCallback() {
        // Cleanup code when section is unmounted
    }
}


// Then register your section with the existing sections container
document.addEventListener('DOMContentLoaded', () => {
    const sections = window.Shopify.designMode ? new SectionContainer() : null;
    if (sections) {
        sections.register("mc-breadcrumbs-section", BreadcrumbsSection);
        sections.register("mc-device-chooser", DeviceChooserSection);
        sections.register("mc-product-compatible-devices", CompatibleDevicesSection);
        sections.register("mc-collapsible-content", HTMLElement);
        sections.register("mc-collection-variant-section", CollectionVariantSection);
        //sections.register("mc-related-products", RelatedProductsSection);
    }
});

document.addEventListener('DOMContentLoaded', function() {
    // Initialize vendor toggles for related products
    const productRelatedCollection = document.querySelector('.product-related-collection');
    if (productRelatedCollection) {
        const toggles = productRelatedCollection.querySelectorAll('[data-vendor-toggle]');
        toggles.forEach(toggle => {
            toggle.addEventListener('click', () => {
                const vendorId = toggle.dataset.vendorToggle;
                const group = productRelatedCollection.querySelector(`[data-vendor-group="${vendorId}"]`);

                // Close all other groups
                productRelatedCollection.querySelectorAll('.vendor-group.is-active').forEach(activeGroup => {
                    if (activeGroup !== group) {
                        activeGroup.classList.remove('is-active');
                        const activeToggle = productRelatedCollection.querySelector(`[data-vendor-toggle="${activeGroup.dataset.vendorGroup}"]`);
                        activeToggle.classList.remove('is-active');
                    }
                });

                // Toggle current group
                toggle.classList.toggle('is-active');
                group.classList.toggle('is-active');
            });
        });
    }
});

/**
 * Flickity-Karussells: Pfeile und Seitenpunkte beschriften.
 *
 * theme.js setzt die aria-labels fest englisch ("Previous"/"Next" bei :2229,
 * "Page dot N" bei :2408). Das Bundle ist vorgebaut und hat keine Quelle im
 * Repo, laesst sich also nicht an der Entstehungsstelle uebersetzen. Deshalb
 * hier nachbessern, sobald die Buttons im DOM sind.
 *
 * Nur aria-labels - sichtbar ist nichts davon, es betrifft Screenreader.
 */
(function () {
  'use strict';

  function relabelCarousels(root) {
    var L = window.languages || {};
    var scope = root || document;

    /* Jedes Label einzeln pruefen: fehlt eines, bleibt Flickitys eigenes
       englisches Label stehen - besser als "undefined" im aria-label. */
    if (L.carouselPrevious) {
      scope.querySelectorAll('.flickity-button.previous').forEach(function (el) {
        el.setAttribute('aria-label', L.carouselPrevious);
      });
    }
    if (L.carouselNext) {
      scope.querySelectorAll('.flickity-button.next').forEach(function (el) {
        el.setAttribute('aria-label', L.carouselNext);
      });
    }
    if (L.carouselPageDot) {
      /* Pro Punkte-Leiste einzeln zaehlen. Ein flaches querySelectorAll ueber
         das ganze Dokument nummeriert sonst durch: das zweite Karussell einer
         Seite begaenne bei "Seite 4" statt "Seite 1". */
      scope.querySelectorAll('.flickity-page-dots').forEach(function (dots) {
        dots.querySelectorAll('.dot').forEach(function (el, i) {
          el.setAttribute('aria-label', L.carouselPageDot.replace('__PAGE__', i + 1));
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { relabelCarousels(); });
  } else {
    relabelCarousels();
  }

  /* Theme-Editor und nachgeladene Sections bauen ihre Karussells neu auf. */
  document.addEventListener('shopify:section:load', function (event) {
    relabelCarousels(event.target);
  });

  /* Mehrere Karussells entstehen erst nach einem fetch, und Flickitys watchCSS
     baut Pfeile und Punkte bei einem Breakpoint-Wechsel neu auf. Beide Faelle
     laufen an DOMContentLoaded und shopify:section:load vorbei, deshalb ein
     Beobachter, der ausschliesslich auf neue Flickity-Bedienelemente reagiert.
     Gebuendelt ueber requestAnimationFrame, damit ein Neuaufbau mit vielen
     Knoten nicht in ebenso viele Durchlaeufe zerfaellt. */
  if (typeof MutationObserver === 'function') {
    var pending = false;
    var observer = new MutationObserver(function (records) {
      if (pending) return;
      var relevant = records.some(function (r) {
        return Array.prototype.some.call(r.addedNodes, function (n) {
          if (n.nodeType !== 1) return false;
          return (n.classList && (n.classList.contains('flickity-button') ||
                                  n.classList.contains('flickity-page-dots'))) ||
                 (n.querySelector && n.querySelector('.flickity-button, .flickity-page-dots'));
        });
      });
      if (!relevant) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        relabelCarousels();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
